import { useCallback, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatFile, ApiFileInfo } from '../types/chat';
import type { useChatApi } from './useChatApi';
type UseChatApiReturnType = ReturnType<typeof useChatApi>;

interface UseChatFilesProps {
  userId: string | null;
  selectedFiles: ChatFile[];
  setSelectedFiles: Dispatch<SetStateAction<ChatFile[]>>;
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  chatApi: UseChatApiReturnType;
}

// Define new type for handlers that require chatId passed explicitly
export type FileEventHandler = (
    files: FileList | File[] | null,
    chatId: string
) => Promise<void>;

export const useChatFiles = ({
  userId,
  selectedFiles,
  setSelectedFiles,
  setIsUploading,
  chatApi,
}: UseChatFilesProps) => {
  const { t } = useTranslation();

  const processFiles = useCallback(async (files: FileList | File[], chatId: string) => {
    console.log('[useChatFiles] processFiles called.');
    if (!chatId || !userId) {
       console.log(`[useChatFiles] processFiles exiting: Missing chatId (${chatId}) or userId (${userId}).`);
       return;
    }
    if (!files || files.length === 0) {
      console.log('[useChatFiles] processFiles exiting: No files provided.');
      return;
    }

    const fileArray = Array.from(files);
    if (fileArray.length === 0) {
       console.log('[useChatFiles] processFiles exiting: File array is empty after Array.from.');
       return;
    }

    // Do not reject large files on the client; server-side route will compress when necessary.
    console.log(`[useChatFiles] Setting isUploading to true. Processing ${fileArray.length} files for chat ${chatId}.`);
    setIsUploading(true);
    const newFilesData: { originalFile: File, uploadedData: ApiFileInfo }[] = []; // Store original file and uploaded metadata

    // --- Step 1: Upload Files ---
    const uploadPromises = fileArray.map(async (file) => {
      console.log(`[useChatFiles] Attempting to upload: ${file.name}`);
      try {
        const uploadedData = await chatApi.uploadFile(file, userId!); // userId checked earlier
        newFilesData.push({ originalFile: file, uploadedData: uploadedData }); // Store both
        console.log(`[useChatFiles] File uploaded successfully: ${uploadedData.name}`, uploadedData);
      } catch (error) {
        console.error(`[useChatFiles] Error uploading file: ${file.name}`, error);
        alert(`${t('error_uploading_file')} ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });

    console.log('[useChatFiles] Waiting for all upload promises...');
    await Promise.all(uploadPromises);
    console.log('[useChatFiles] All upload promises settled.');

    // --- Step 2: Captioning disabled for uploaded files (commented out) ---
    // const captionPromises = newFilesData.map(async ({ originalFile, uploadedData }) => { ... });
    // await Promise.all(captionPromises);
    // Instead, just construct ChatFile objects without captions.
    const finalChatFiles: ChatFile[] = [];
    for (const { uploadedData } of newFilesData) {
      const chatFile: ChatFile = {
        id: uploadedData.id,
        name: uploadedData.name,
        type: uploadedData.type,
        extension: uploadedData.extension,
        size: uploadedData.size,
        url: uploadedData.url,
        created_at: uploadedData.created_at,
        // caption intentionally omitted
      };
      finalChatFiles.push(chatFile);
    }
    console.log('[useChatFiles] Captioning is disabled for uploaded files; adding files without captions.');

    console.log('[useChatFiles] Current selectedFiles (before update):', selectedFiles);
    console.log('[useChatFiles] Files successfully processed (uploaded & captioned):', finalChatFiles);

    // --- Step 3: Update State ---
    if (finalChatFiles.length > 0) {
      console.log('[useChatFiles] Calling setSelectedFiles to add new files...');
      setSelectedFiles(prev => {
        // Prevent adding duplicates if the same file was somehow processed twice
        const existingIds = new Set(prev.map(f => f.id));
        const filesToAdd = finalChatFiles.filter(f => !existingIds.has(f.id));
        const updatedFiles = [...prev, ...filesToAdd];
        console.log('[useChatFiles] setSelectedFiles update function executed. New state:', updatedFiles);
        return updatedFiles;
      });
    } else {
       console.log('[useChatFiles] No new files were successfully processed, skipping setSelectedFiles.');
    }

    console.log('[useChatFiles] Setting isUploading to false.');
    setIsUploading(false);
    console.log('[useChatFiles] processFiles finished.');

  }, [ userId, chatApi, t, setIsUploading, setSelectedFiles, selectedFiles]); // <-- Use chatApi dependency

  const handleFileSelect: FileEventHandler = useCallback(async (files: FileList | File[] | null, chatId: string) => {
    console.log('[useChatFiles] handleFileSelect called for chatId:', chatId);
    if (!files || files.length === 0) {
       console.log('[useChatFiles] handleFileSelect exiting: No files provided.');
       return;
    }
    await processFiles(files, chatId);
    console.log('[useChatFiles] handleFileSelect finished.');
  }, [processFiles]);

  const handleFileDrop: FileEventHandler = useCallback(async (files: FileList | File[] | null, chatId: string) => {
      console.log('[useChatFiles] handleFileDrop called for chatId:', chatId);
      if (!files || files.length === 0) {
        console.log('[useChatFiles] handleFileDrop exiting: No files provided.');
        return;
      }
      await processFiles(files, chatId);
      console.log('[useChatFiles] handleFileDrop finished.');
  }, [processFiles]);

  const removeFile = useCallback((fileId: string) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  }, [setSelectedFiles]);

  return {
    selectedFiles,
    setSelectedFiles,
    handleFileSelect,
    handleFileDrop,
    removeFile,
  };
};

// Exported type remains the same
export interface UseChatFilesReturnType {
    selectedFiles: ChatFile[];
    setSelectedFiles: Dispatch<SetStateAction<ChatFile[]>>;
    handleFileSelect: FileEventHandler;
    handleFileDrop: FileEventHandler;
    removeFile: (fileId: string) => void;
    processUrlsInMessage: (message: string, chatId: string) => Promise<ChatFile[]>;
} 